import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CouponCategory } from "@/utils/businessCoupon";

type CouponType = "percent" | "amount";

export type BusinessCouponTermsInputs = {
  couponCategory: CouponCategory;
  couponType: CouponType;
  requiredDiscountValue: string;

  // Category-dependent fields
  buyQty: string;
  getQty: string;
  freeItemDetail: string;
  bundleDetail: string;
  timeSlotStart: string;
  timeSlotEnd: string;
  timeSlotDetail: string;
  groupMinPeople: string;
  groupDealDetail: string;
  reservationAdvanceDays: string;
  reservationDetail: string;
  freeUpgradeDetail: string;
  addOnDetail: string;
  pointsMultiplierValue: string;
  birthdayDetail: string;
  depositWaiverDetail: string;
  firstTimeDetail: string;

  // Expiry
  expiryDate: string;
  hasExpiryPeriod: boolean;
  expiryPeriod: string;

  // Extra
  extraTerms: string;
};

/**
 * Web port of the RN `useBusinessCouponTerms`: turns the category-specific form
 * inputs into the bilingual terms & conditions stored on the coupon, plus a
 * live preview in the reader's language.
 */
export const useBusinessCouponTerms = (inputs: BusinessCouponTermsInputs) => {
  const { t: tBase, i18n } = useTranslation();

  const tString = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const result = tBase(key, options);
      return typeof result === "string" ? result : "";
    },
    [tBase],
  );

  const t = useCallback(
    (key: string, params?: Record<string, unknown>, lng?: string): string =>
      tString(key, { ...(params ?? {}), ...(lng ? { lng } : {}) }),
    [tString],
  );

  const buildCategoryTerm = useCallback(
    (lng?: string) => {
      const {
        couponCategory,
        couponType,
        requiredDiscountValue,
        buyQty,
        getQty,
        freeItemDetail,
        bundleDetail,
        timeSlotStart,
        timeSlotEnd,
        timeSlotDetail,
        groupMinPeople,
        groupDealDetail,
        reservationAdvanceDays,
        reservationDetail,
        freeUpgradeDetail,
        addOnDetail,
        pointsMultiplierValue,
        birthdayDetail,
        depositWaiverDetail,
        firstTimeDetail,
      } = inputs;

      switch (couponCategory) {
        case "discount":
          if (!requiredDiscountValue) return "";
          return couponType === "percent"
            ? t("Terms_Discount_Percent", { value: requiredDiscountValue }, lng)
            : t("Terms_Discount_Amount", { value: requiredDiscountValue }, lng);
        case "cashback":
          if (!requiredDiscountValue) return "";
          return couponType === "percent"
            ? t("Terms_Cashback_Percent", { value: requiredDiscountValue }, lng)
            : t("Terms_Cashback_Amount", { value: requiredDiscountValue }, lng);
        case "bogo":
          if (!buyQty || !getQty) return "";
          return t("Terms_BOGO", { buy: buyQty, get: getQty }, lng);
        case "freeItem":
          if (!freeItemDetail) return "";
          return t("Terms_FreeItem", { detail: freeItemDetail }, lng);
        case "freeShipping":
          return t("Terms_FreeShipping", undefined, lng);
        case "bundle":
          if (!bundleDetail) return "";
          return t("Terms_Bundle", { detail: bundleDetail }, lng);
        case "timeSlotDeal":
          if (!timeSlotStart || !timeSlotEnd || !timeSlotDetail) return "";
          return t(
            "Terms_TimeSlotDeal",
            { start: timeSlotStart, end: timeSlotEnd, detail: timeSlotDetail },
            lng,
          );
        case "groupDeal":
          if (!groupMinPeople || !groupDealDetail) return "";
          return t(
            "Terms_GroupDeal",
            { min: groupMinPeople, detail: groupDealDetail },
            lng,
          );
        case "reservationDiscount":
          if (!reservationAdvanceDays || !reservationDetail) return "";
          return t(
            "Terms_ReservationDiscount",
            { days: reservationAdvanceDays, detail: reservationDetail },
            lng,
          );
        case "freeUpgrade":
          if (!freeUpgradeDetail) return "";
          return t("Terms_FreeUpgrade", { detail: freeUpgradeDetail }, lng);
        case "addOn":
          if (!addOnDetail) return "";
          return t("Terms_AddOn", { detail: addOnDetail }, lng);
        case "pointsMultiplier":
          if (!pointsMultiplierValue) return "";
          return t(
            "Terms_PointsMultiplier",
            { multiplier: pointsMultiplierValue },
            lng,
          );
        case "birthday":
          if (!birthdayDetail) return "";
          return t("Terms_Birthday", { detail: birthdayDetail }, lng);
        case "depositWaiver":
          if (!depositWaiverDetail) return "";
          return t("Terms_DepositWaiver", { detail: depositWaiverDetail }, lng);
        case "firstTime":
          if (!firstTimeDetail) return "";
          return t("Terms_FirstTime", { detail: firstTimeDetail }, lng);
        default:
          return "";
      }
    },
    [inputs, t],
  );

  const generatedTerms = useMemo(
    () => buildCategoryTerm(),
    [buildCategoryTerm, i18n.language],
  );

  const expiryDateLine = useMemo(
    () =>
      inputs.expiryDate
        ? t("Terms_ExpiryDate", { date: inputs.expiryDate })
        : "",
    [inputs.expiryDate, t, i18n.language],
  );

  const expiryPeriodLine = useMemo(
    () =>
      inputs.hasExpiryPeriod && inputs.expiryPeriod
        ? t("Terms_ExpiryPeriodDays", { days: inputs.expiryPeriod })
        : "",
    [inputs.hasExpiryPeriod, inputs.expiryPeriod, t, i18n.language],
  );

  const missingRequiredTerms = !generatedTerms;

  const previewTerms = useMemo(
    () =>
      [generatedTerms, expiryDateLine, expiryPeriodLine]
        .filter(Boolean)
        .join("\n"),
    [generatedTerms, expiryDateLine, expiryPeriodLine],
  );

  const previewFooter = useMemo(
    () => t("Terms_UninfohkOfficialLaw"),
    [t, i18n.language],
  );

  const previewMainText = useMemo(
    () => previewTerms || t("Please fill in Offer Details"),
    [previewTerms, t, i18n.language],
  );

  const isPreviewFallback = !previewTerms;

  const buildTermsAndConditions = useCallback(
    (lng: "en" | "zh_hant") => {
      const category = buildCategoryTerm(lng);
      const dateLine = inputs.expiryDate
        ? t("Terms_ExpiryDate", { date: inputs.expiryDate }, lng)
        : "";
      const periodLine =
        inputs.hasExpiryPeriod && inputs.expiryPeriod
          ? t("Terms_ExpiryPeriodDays", { days: inputs.expiryPeriod }, lng)
          : "";
      const footer = t("Terms_UninfohkOfficialLaw", undefined, lng);

      return [
        category,
        dateLine,
        periodLine,
        inputs.extraTerms,
        footer,
      ]
        .filter(Boolean)
        .join("\n");
    },
    [buildCategoryTerm, inputs.extraTerms, inputs.expiryDate, inputs.expiryPeriod, inputs.hasExpiryPeriod, t],
  );

  return {
    generatedTerms,
    missingRequiredTerms,
    previewTerms,
    previewFooter,
    previewMainText,
    isPreviewFallback,
    termsAndConditionsEn: useMemo(
      () => buildTermsAndConditions("en"),
      [buildTermsAndConditions],
    ),
    termsAndConditionsZhHant: useMemo(
      () => buildTermsAndConditions("zh_hant"),
      [buildTermsAndConditions],
    ),
  };
};
